// permissions_ui.ts — Helpers front pour la gestion des permissions.
//
// Migration S2.3 du plan TS+Vite (2026-05-10) : permissions_ui.js → .ts strict.
//
// Chargé APRÈS auth_ui.ts (qui pose window.__authUser) et AVANT les pages.
//
// API exposée (globale pour rester compatible avec le code non-modulaire) :
//   - hasPermission(code)            → bool
//   - hasAnyPermission(codes)        → bool
//   - requirePermOrRedirect(code)    → bool ; si false, redirige home + toast
//   - applyNavPermissionGating()     → masque les items de nav sans perm
//   - applyMobileNavHiding()         → masque les items réservés au desktop
//   - onAuthReady(cb)                → appelle cb() dès que __authUser est posé
//   - isMobile()                     → bool
//
// Convention : chaque <a> de la sidebar porte un attribut data-perm="projects.read"
// (ou plusieurs codes séparés par espaces, interprétés en OR). Les items sans
// data-perm restent toujours visibles (ex : Dashboard/home).
//
// Groupement par domaine pour la matrice de perms (page Groupes) :
// voir PERMISSION_DOMAINS ci-dessous. Les codes inconnus tombent dans "Autres".

interface PermissionDomain {
  code: string;
  label: string;
}

interface PermissionedAuthUser {
  permissions?: readonly string[];
  role?: string;
  tenant_type?: string;
  code_client?: string;
  [key: string]: unknown;
}

declare global {
  // Toutes optionnelles pour aligner avec window.d.ts qui pré-déclare
  // hasPermission en optionnel. À fusionner ici quand window.d.ts sera
  // retiré (S4-S5).
  interface Window {
    hasPermission?: (code: string) => boolean;
    hasAnyPermission?: (codes: readonly string[]) => boolean;
    requirePermOrRedirect?: (code: string, opts?: { label?: string }) => boolean;
    applyNavPermissionGating?: () => void;
    applyMobileNavHiding?: () => void;
    onAuthReady?: (cb: () => void) => void;
    isMobile?: () => boolean;
    isSoloUser?: () => boolean;
    MOBILE_HIDDEN_ROUTES?: Set<string>;
    PERMISSION_DOMAINS?: readonly PermissionDomain[];
    PERMISSION_ACTION_LABELS?: Record<string, string>;
  }
}

(function () {
  'use strict';

  // ── Catalogue de domaines (pour la page Groupes) ─────────────────────────
  // Ordre d'affichage voulu. Libellés FR. `actions` : ce sont les codes
  // suffixes après le domaine (read, write, analyze, ...).
  const PERMISSION_DOMAINS: readonly PermissionDomain[] = [
    { code: 'projects', label: 'Projets' },
    { code: 'revisions', label: 'Révisions' },
    { code: 'programs', label: 'Programmes' },
    { code: 'actions', label: 'Liste des actions' },
    { code: 'action_plans', label: "Plans d'action" },
    { code: 'bugs', label: 'Suivi des bugs' },
    { code: 'risk_lists', label: 'Listes de risques' },
    { code: 'checklists', label: 'Checklists' },
    { code: 'stages', label: 'Étapes' },
    { code: 'action_statuses', label: 'États des actions' },
    { code: 'risk_colors', label: 'Couleurs des risques' },
    { code: 'generic_roles', label: 'Rôles génériques' },
    { code: 'postits', label: 'Post-it' },
    { code: 'users', label: 'Utilisateurs' },
    { code: 'structures', label: 'Structures' },
    { code: 'user_groups', label: 'Groupes' },
    { code: 'meetings', label: 'Analyse de CR' },
    { code: 'meeting_indicators', label: 'Indicateurs de réunion' },
    { code: 'committee_reminders', label: 'Relances de comité' },
    { code: 'exports', label: 'Exports enrichis' },
    { code: 'mascot', label: 'Professeur Ornaxion' },
  ];

  const ACTION_LABELS: Record<string, string> = {
    read: 'Lecture',
    write: 'Écriture',
    analyze: 'Analyser',
    import: 'Importer',
    matrix: 'Matrice Eisenhower',
    rich: 'Exports enrichis',
  };

  function _perms(): readonly string[] | null {
    const u = window.__authUser as PermissionedAuthUser | null | undefined;
    if (!u || !Array.isArray(u.permissions)) return null;
    return u.permissions;
  }

  // ── API publique ──────────────────────────────────────────────────────────

  function hasPermission(code: string): boolean {
    if (!code) return true;
    const perms = _perms();
    if (!perms) return false; // pas connecté ou perms pas encore chargées
    // Le backend renvoie une liste DÉJÀ plafonnée par le palier d'abonnement
    // (même pour un admin : ALL ∩ palier). On s'aligne strictement sur cette
    // liste — surtout PAS de bypass `role === 'admin'`, sinon un admin d'un
    // tenant Essentiel verrait des CTA/menus hors de son palier.
    return perms.includes(code);
  }

  function hasAnyPermission(codes: readonly string[]): boolean {
    if (!Array.isArray(codes) || codes.length === 0) return true;
    return codes.some((c) => hasPermission(c));
  }

  function isSoloUser(): boolean {
    const u = window.__authUser as PermissionedAuthUser | null | undefined;
    return u?.tenant_type === 'solo';
  }

  function requirePermOrRedirect(code: string, opts?: { label?: string }): boolean {
    const options = opts || {};
    if (hasPermission(code)) return true;

    // Pas d'accès : toast + redirection home.
    const label = options.label || 'cette page';
    try {
      const t = (window as unknown as { toast?: (msg: string, kind: string) => void }).toast;
      if (typeof t === 'function') {
        t(`Accès refusé : vous n'avez pas la permission pour ${label}.`, 'error');
      }
    } catch {
      /* ignore */
    }
    try {
      // setTimeout pour laisser le toast apparaître avant le reflow.
      setTimeout(() => {
        window.location.hash = '#dashboard';
      }, 10);
    } catch {
      /* ignore */
    }
    return false;
  }

  function applyNavPermissionGating(): void {
    // Masque chaque <a> de la sidebar dont data-perm n'est pas satisfait.
    const links = document.querySelectorAll<HTMLAnchorElement>('#sidebar nav a[data-perm]');
    links.forEach((a) => {
      const raw = (a.getAttribute('data-perm') || '').trim();
      if (!raw) {
        a.style.display = '';
        return;
      }
      const codes = raw.split(/\s+/);
      const ok = hasAnyPermission(codes);
      a.style.display = ok ? '' : 'none';
    });

    // Masque aussi les section-label orphelines (aucun enfant visible
    // avant la prochaine section-label).
    const nav = document.querySelector('#sidebar nav');
    if (!nav) return;
    const children = Array.from(nav.children) as HTMLElement[];
    for (let i = 0; i < children.length; i++) {
      const el = children[i];
      if (!el || !el.classList.contains('section-label')) continue;
      let hasVisible = false;
      for (let j = i + 1; j < children.length; j++) {
        const next = children[j];
        if (!next) break;
        if (next.classList.contains('section-label')) break;
        if (next.tagName === 'A' && next.style.display !== 'none') {
          hasVisible = true;
          break;
        }
      }
      el.style.display = hasVisible ? '' : 'none';
    }
  }

  // Callback quand l'auth est prête (utile pour app.js).
  const _ready: Array<() => void> = [];
  function onAuthReady(cb: () => void): void {
    const u = window.__authUser as PermissionedAuthUser | null | undefined;
    if (u && Array.isArray(u.permissions)) {
      cb();
      return;
    }
    _ready.push(cb);
  }

  // auth_ui.ts dispatch `auth:ready` après avoir posé __authUser. On écoute
  // aussi le cas où __authUser est posé plus tard (la bootstrap() de
  // auth_ui.ts est async).
  // Liens du menu à cacher en mode US (pas de perm associée côté backend,
  // donc on les masque applicativement). Sélecteurs CSS.
  const SOLO_HIDDEN_NAV_SELECTORS = ['#nav-notifications-link'];

  function applySoloNavHiding(): void {
    if (!isSoloUser()) return;
    for (const sel of SOLO_HIDDEN_NAV_SELECTORS) {
      const el = document.querySelector<HTMLElement>(sel);
      if (el) el.style.display = 'none';
    }
  }

  window.addEventListener('auth:ready', () => {
    applyNavPermissionGating();
    applySoloNavHiding();
    applyMobileNavHiding();
    document.body.classList.toggle('is-mobile', isMobile());
    document.body.classList.toggle('is-solo', isSoloUser());
    while (_ready.length > 0) {
      // Chaque callback enregistré via onReady() est best-effort :
      // si l'un plante, on log et on continue avec les suivants.
      const cb = _ready.shift();
      if (!cb) continue;
      try {
        cb();
      } catch (e) {
        console.warn('[permissions_ui] onReady callback', e);
      }
    }
  });

  // ── Mobile & mode lecture seule ─────────────────────────────────────────
  // Le viewport ≤768px enclenche :
  //   - le mode lecture seule sur TOUTES les pages sauf post-it,
  //   - le masquage des items de nav admin (matrice de perms, groupes, etc.),
  //   - le drawer burger.
  // Géré par CSS (media queries) pour le layout et le bandeau. Côté JS on
  // expose un helper `isMobile()` pour que `app.js` guarde les routes admin
  // et saute le masquage sur les pages post-it.

  const MOBILE_MEDIA = '(max-width: 768px)';
  function isMobile(): boolean {
    try {
      return window.matchMedia(MOBILE_MEDIA).matches;
    } catch {
      return false;
    }
  }

  // Les pages réservées à l'admin — inaccessibles en mobile (option A).
  // Correspondance avec les hash-routes du router dans app.js.
  // Avril 2026 : liste vidée pour permettre la consultation de toutes
  // les pages en mobile (Programme, paramétrages divers, etc.). L'édition
  // reste bloquée par le mode mobile-readonly (boutons cachés, formulaires
  // grisés). Les seules pages éditables en mobile sont #postits et
  // #actions (cf. core.js dispatch).
  const MOBILE_HIDDEN_ROUTES = new Set<string>([]);

  function applyMobileNavHiding(): void {
    if (!isMobile()) return;
    const links = document.querySelectorAll<HTMLAnchorElement>('#sidebar nav a');
    links.forEach((a) => {
      const href = a.getAttribute('href') || '';
      if (MOBILE_HIDDEN_ROUTES.has(href)) {
        a.style.display = 'none';
      }
    });
  }

  // ── Bandeau "Mobile — Mode restreint" : fermeture + réaffichage ─────────
  // L'utilisateur peut fermer le bandeau via le ✕. La fermeture vaut pour la
  // SESSION courante (sessionStorage) : un simple rechargement ne le réaffiche
  // pas, mais une nouvelle connexion le réaffiche (auth_ui.ts purge le flag au
  // login). Géré par la classe body.readonly-banner-dismissed (cf. app.css).
  const BANNER_DISMISS_KEY = 'ornexia.readonlyBannerDismissed';

  function isBannerDismissed(): boolean {
    try {
      return sessionStorage.getItem(BANNER_DISMISS_KEY) === '1';
    } catch {
      return false;
    }
  }

  function setupReadonlyBanner(): void {
    document.body.classList.toggle('readonly-banner-dismissed', isBannerDismissed());
    const closeBtn = document.getElementById('readonly-banner-close');
    if (closeBtn && !closeBtn.dataset['wired']) {
      closeBtn.dataset['wired'] = '1';
      closeBtn.addEventListener('click', () => {
        try {
          sessionStorage.setItem(BANNER_DISMISS_KEY, '1');
        } catch {
          /* sessionStorage indispo : on masque quand même pour la vue courante. */
        }
        document.body.classList.add('readonly-banner-dismissed');
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setupReadonlyBanner);
  } else {
    setupReadonlyBanner();
  }

  // Réapplique tout quand la taille de la fenêtre change (rotation, resize).
  window.addEventListener('resize', () => {
    applyNavPermissionGating();
    applyMobileNavHiding();
    // Met à jour le flag CSS body.mobile-readonly — app.js le pose à chaque
    // dispatch de route (page courante), ici on actualise seulement la
    // dimension "mobile".
    document.body.classList.toggle('is-mobile', isMobile());
  });

  // Expose API globale.
  window.hasPermission = hasPermission;
  window.hasAnyPermission = hasAnyPermission;
  window.requirePermOrRedirect = requirePermOrRedirect;
  window.applyNavPermissionGating = applyNavPermissionGating;
  window.applyMobileNavHiding = applyMobileNavHiding;
  window.onAuthReady = onAuthReady;
  window.isMobile = isMobile;
  window.isSoloUser = isSoloUser;
  window.MOBILE_HIDDEN_ROUTES = MOBILE_HIDDEN_ROUTES;
  window.PERMISSION_DOMAINS = PERMISSION_DOMAINS;
  window.PERMISSION_ACTION_LABELS = ACTION_LABELS;
})();

// Marker module ESM (pas d'export public, juste effet de bord + window.X).
export {};
